import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

const MAX_CHARS = 50000;
const MIN_CHARS = 3;

interface ImproveWithAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (instructions: string) => void;
  isLoading?: boolean;
  error?: string;
}

/**
 * Modal "Construir com IA" — gera ou reescreve o HTML do template de email via IA
 * Mesmo padrão visual do TrainAgentModal
 */
export const ImproveWithAIModal: React.FC<ImproveWithAIModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isLoading = false,
  error: externalError,
}) => {
  const [instructions, setInstructions] = useState('');
  const [localError, setLocalError] = useState('');

  const error = externalError || localError;
  const charCount = instructions.length;

  if (!isOpen) return null;

  const validate = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.length < MIN_CHARS) return `Mínimo de ${MIN_CHARS} caracteres (atual: ${trimmed.length})`;
    if (trimmed.length > MAX_CHARS) return `Máximo de ${MAX_CHARS.toLocaleString('pt-BR')} caracteres excedido`;
    return null;
  };

  const handleGenerate = () => {
    const validationError = validate(instructions);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    if (instructions.trim()) {
      setLocalError('');
      onGenerate(instructions.trim());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInstructions(e.target.value);
    if (localError) setLocalError('');
  };

  const handleClose = () => {
    if (!isLoading) {
      setInstructions('');
      setLocalError('');
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden"
        style={{
          backgroundColor: '#ffffff',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#ede9fe' }}>
              <Icon name="zap" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Construir com IA
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                Descreva o email que você quer, ou o que mudar no HTML atual
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            disabled={isLoading}
            className="p-2 rounded-lg transition-colors disabled:opacity-50"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div
            className="flex items-start gap-3 p-4 rounded-lg mb-4"
            style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}
          >
            <Icon name="zap" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#1e40af' }}>Como funciona?</p>
              <p className="text-sm mt-1" style={{ color: '#1e40af' }}>
                A IA constrói o HTML do email a partir da sua descrição. Se já houver HTML, ela
                parte dele e aplica o que você pedir. O resultado vai para a aba HTML e aparece no preview.
              </p>
            </div>
          </div>

          <div>
            <Textarea
              label="Descreva o que você precisa alterar no template"
              description={!error ? 'Seja específico sobre as alterações desejadas para melhores resultados.' : undefined}
              error={error}
              value={instructions}
              onChange={handleChange}
              placeholder="Ex: crie um email de boas-vindas com o logo no topo e um botão de CTA; ou: mude a cor do header para azul..."
              rows={6}
              disabled={isLoading}
              resize="none"
              maxLength={MAX_CHARS}
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs" style={{ color: charCount > MAX_CHARS ? '#ef4444' : '#94a3b8' }}>
                {charCount.toLocaleString('pt-BR')}/{MAX_CHARS.toLocaleString('pt-BR')}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
        >
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
            style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e2e8f0' }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Cancelar
          </button>

          <button
            onClick={handleGenerate}
            disabled={isLoading || !instructions.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#8b5cf6' }}
            onMouseEnter={(e) => { if (!isLoading && instructions.trim()) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
          >
            {isLoading ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processando...</>
            ) : (
              <><Icon name="zap" className="w-4 h-4" />Construir</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
